/**
 * WelcomeModal — First-run 4-step introduction for new First Closers.
 *
 * Fires the first time a user opens `/v3` after signup. Marks itself
 * seen via POST /api/v3/onboarding/welcome-seen — never shows again.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import {
  KeyRound, Sparkles, Brain, MessageSquare, Handshake, TrendingUp,
  ArrowRight, X, BookOpen,
} from "lucide-react";

const SLIDES = [
  {
    icon: KeyRound,
    kicker: "Welcome, First Closer",
    title: "This is your operating system for closing deals.",
    body: "You've got 100 free lead credits waiting in your Marketplace. Here's the 60-second tour of the four moves that make iSpeedToClose different.",
    accent: "linear-gradient(135deg, #F59E0B, #EA580C, #8B5CF6)",
  },
  {
    icon: BookOpen,
    kicker: "First — read the book behind the software",
    title: "The Wealth Blueprint. 76 pages. Free.",
    body: "Tracy wrote the family-office playbook the Perrin enterprise actually runs — entity stack, tax wrappers, capital partnerships, stewardship. Same tools; different outcomes when you know the philosophy first.",
    accent: "linear-gradient(135deg, #B45309, #78350F)",
    ctaLabel: "Grab the Blueprint (PDF)",
    ctaHref: "/wealth-blueprint",
  },
  {
    icon: Sparkles,
    kicker: "Step 1 · Pull motivated leads",
    title: "Spend credits, get pre-filtered sellers.",
    body: "Pick a market + filter preset (high-equity absentees, pre-foreclosure, tired landlords…). Credits are only charged for leads that land in your CRM.",
    accent: "linear-gradient(135deg, #F59E0B, #EA580C)",
  },
  {
    icon: Brain,
    kicker: "Step 2 · Let AI rank them",
    title: "Every seller is scored + explained.",
    body: "Acquisition Intelligence grades motivation, heat, and next-best-action for every lead — with the reasons why. No more guessing which one to call first.",
    accent: "linear-gradient(135deg, #10B981, #059669)",
  },
  {
    icon: MessageSquare,
    kicker: "Step 3 · Blast + Convert",
    title: "1-click SMS + AI-recommended offers + exits.",
    body: "Fire personalized outreach with Claude-powered openers, then use Offer Intelligence (5 strategies) and Exit Intelligence (8 strategies) to lock in the win — with a Claude coach walking you through each play.",
    accent: "linear-gradient(135deg, #8B5CF6, #6D28D9)",
  },
];

export default function WelcomeModal({ onClose }) {
  const [step, setStep] = useState(0);
  const [busy, setBusy] = useState(false);

  const slide = SLIDES[step];
  const Icon = slide.icon;
  const last = step === SLIDES.length - 1;

  const complete = async () => {
    if (busy) return;
    setBusy(true);
    try { await api.post("/v3/onboarding/welcome-seen"); }
    catch { /* silent */ }
    finally { onClose?.(); }
  };

  const next = () => (last ? complete() : setStep((s) => s + 1));

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") complete(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4"
         data-testid="v3-welcome-modal"
         role="dialog" aria-modal="true">
      <button type="button" onClick={complete} aria-label="Close welcome"
              className="absolute inset-0 bg-slate-900/70 backdrop-blur-sm" />
      <div className="relative w-full max-w-lg rounded-2xl bg-white shadow-2xl overflow-hidden border border-slate-200">
        <div className="h-1.5 w-full" style={{ backgroundImage: slide.accent }} />

        <button type="button" onClick={complete}
                data-testid="v3-welcome-close" aria-label="Close"
                className="absolute top-3 right-3 rounded-full hover:bg-slate-100 p-2 z-10">
          <X className="w-4 h-4 text-slate-500" />
        </button>

        <div className="p-8">
          <div className="flex items-center gap-3">
            <span className="grid place-items-center h-11 w-11 rounded-xl text-white shadow"
                  style={{ backgroundImage: slide.accent }}>
              <Icon className="w-5 h-5" />
            </span>
            <div className="text-[10px] font-black tracking-widest uppercase text-slate-500">
              {slide.kicker}
            </div>
          </div>

          <h2 className="mt-4 text-2xl md:text-3xl font-bold text-slate-900 leading-tight tracking-tight">
            {slide.title}
          </h2>
          <p className="mt-3 text-[14.5px] leading-relaxed text-slate-600">
            {slide.body}
          </p>

          <div className="mt-6 flex items-center gap-1.5">
            {SLIDES.map((_, i) => (
              <span key={i}
                    className={`h-1.5 rounded-full transition-all ${i === step ? "w-8 bg-slate-900" : "w-2 bg-slate-200"}`} />
            ))}
          </div>

          <div className="mt-6 flex items-center justify-between gap-3">
            <button type="button" onClick={complete}
                    data-testid="v3-welcome-skip"
                    className="text-[12px] font-semibold text-slate-500 hover:text-slate-700 transition-colors">
              Skip intro
            </button>
            <div className="flex items-center gap-2">
              {slide.ctaHref && (
                <a href={slide.ctaHref} target="_blank" rel="noopener noreferrer"
                   onClick={() => { /* keep modal open — user comes back and clicks Next */ }}
                   data-testid="v3-welcome-slide-cta"
                   className="inline-flex items-center gap-1.5 rounded-lg text-slate-900 bg-white border border-slate-300 text-[12px] font-bold px-3 py-2 hover:bg-slate-50 transition-colors">
                  <BookOpen className="w-3.5 h-3.5" />
                  {slide.ctaLabel || "Open"}
                </a>
              )}
              <button type="button" onClick={next} disabled={busy}
                      data-testid="v3-welcome-next"
                      className="inline-flex items-center gap-2 rounded-lg text-white text-sm font-bold px-5 py-2.5 shadow disabled:opacity-60"
                      style={{ backgroundImage: slide.accent }}>
                {last ? "Get started" : "Next"} <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>

        {last && (
          <div className="border-t border-slate-100 bg-slate-50 px-8 py-4 flex flex-wrap items-center justify-between gap-2 text-[12px]">
            <span className="text-slate-500">Deep-link straight in:</span>
            <div className="flex items-center gap-2">
              <a href="/wealth-blueprint" target="_blank" rel="noopener noreferrer"
                 onClick={complete}
                 className="inline-flex items-center gap-1 text-amber-800 font-semibold hover:underline">
                <BookOpen className="w-3 h-3" /> Blueprint
              </a>
              <Link to="/v3/marketplace" onClick={complete}
                    className="inline-flex items-center gap-1 text-amber-700 font-semibold hover:underline">
                <Sparkles className="w-3 h-3" /> Marketplace
              </Link>
              <Link to="/v3/intel/acquisition-intel" onClick={complete}
                    className="inline-flex items-center gap-1 text-emerald-700 font-semibold hover:underline">
                <Brain className="w-3 h-3" /> Intelligence
              </Link>
              <Link to="/v3/intel/offer-intel" onClick={complete}
                    className="inline-flex items-center gap-1 text-orange-700 font-semibold hover:underline">
                <Handshake className="w-3 h-3" /> Offers
              </Link>
              <Link to="/v3/intel/exit-intel" onClick={complete}
                    className="inline-flex items-center gap-1 text-emerald-700 font-semibold hover:underline">
                <TrendingUp className="w-3 h-3" /> Exits
              </Link>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
